import Image from 'next/image';
import { resolveImageAltText } from '@/lib/utils';
import {
  MobileSvgPause,
  MobileSvgPlay,
  SvgNextCaret,
  SvgPause,
  SvgPlay,
  SvgPrevCaret,
  ThumbnailSvgPlay
} from '../Icons';

export const THUMB_SLIDE_CLASS =
  'relative block w-[calc((100%-40px)/3)] shrink-0 overflow-hidden md:w-[calc((100%-80px)/5)]';

const ARROW_VARIANTS = {
  prev: { side: 'left-5', Caret: SvgPrevCaret },
  next: { side: 'right-5', Caret: SvgNextCaret }
};

function slideLabel(direction) {
  return `Go to ${direction === 'prev' ? 'previous' : 'next'} slide`;
}

export function FeaturedImageFrame({ children }) {
  return (
    <div className="featured-image group relative mb-4 self-start">
      {children}
    </div>
  );
}

export function GalleryThumbnailsFrame({ children }) {
  return (
    <div className="gallery-thumbnails flex min-h-15 max-w-none sm:mx-0 sm:justify-center md:mx-auto lg:w-full">
      {children}
    </div>
  );
}

// The `blaze-prev`/`blaze-next` classes supply the arrow mask-image (styles/slider.css).
export function ThumbStripButton({ direction, onClick }) {
  return (
    <button
      className={`blaze-${direction} block h-12 min-w-12 self-center bg-black`}
      aria-label={slideLabel(direction)}
      disabled={!onClick}
      onClick={onClick}
    />
  );
}

// Overlays the featured image; fades in on hover of the FeaturedImageFrame group.
export function GalleryArrow({ direction, onClick }) {
  const { side, Caret } = ARROW_VARIANTS[direction];

  return (
    <div
      className={`blaze-${direction}-circle absolute top-[46%] ${side} z-40 mr-5 h-10 w-10 cursor-pointer rounded-full bg-white opacity-0 transition-opacity! group-hover:opacity-80 group-hover:duration-700 group-hover:ease-in sm:block lg:block lg:min-w-10`}
    >
      <button
        className={`blaze-${direction}-arrow z-10 block h-10 w-10`}
        aria-label={slideLabel(direction)}
        onClick={onClick}
      >
        <Caret />
      </button>
    </div>
  );
}

// Play/pause toggle over the active video slide. Desktop adds a dimming
// overlay: always-on when paused, hover-only when playing.
export function VideoControls({ playing, onPlay, onPause }) {
  let toggleControl;

  if (playing) {
    toggleControl = (
      <>
        <MobileSvgPause handlePause={onPause} classes={'min-lg:hidden'} />
        <SvgPause
          handlePause={onPause}
          classes={'max-lg:hidden lg:group-hover:block'}
        />
      </>
    );
  } else {
    toggleControl = (
      <>
        <MobileSvgPlay handlePlay={onPlay} classes={'min-lg:hidden'} />
        <SvgPlay
          handlePlay={onPlay}
          classes={'max-lg:hidden lg:group-hover:block'}
        />
      </>
    );
  }

  return (
    <div className="absolute top-0 right-0 z-10 flex h-full w-full">
      {toggleControl}
      <div
        className={`video-pause-overlay absolute top-0 right-0 z-40 hidden h-full w-full lg:block ${
          playing
            ? 'opacity-0 group-hover:bg-black group-hover:opacity-20'
            : 'bg-black opacity-20'
        }`}
      />
    </div>
  );
}

export function GalleryThumb({ image, isActive = false, hasVideo = false }) {
  return (
    <div
      className={`relative aspect-[4/3] ${isActive ? 'border-fiery-cognac border-2' : ''}`}
    >
      <Image
        src={image.url}
        width={120}
        height={90}
        alt={resolveImageAltText(image)}
        sizes="120px"
        className="h-full w-full object-cover"
      />
      {hasVideo && (
        <div className="absolute inset-0 flex">
          <ThumbnailSvgPlay />
        </div>
      )}
    </div>
  );
}
